import styled, { css } from 'styled-components';
import { numberWithUnit } from '../../../utils/string-utils';
import { BaSeTheme } from './../../../theme/index';

export type StyledButtonGroupAlign = 'start' | 'center' | 'end';
export type StyledButtonGroupDisposition = 'horizontal' | 'vertical';
export type StyledButtonGroupType = 'checkbox' | 'radio' | 'button';
export type StyledButtonGroupBorderRadius = number | string;

export interface StyledButtonGroup {
  gap: number;
  align: StyledButtonGroupAlign;
  disposition: StyledButtonGroupDisposition;
}

export interface StyledButtonWrapper
  extends Pick<StyledButtonGroup, 'disposition' | 'gap'> {
  firstItem: boolean;
  lastItem: boolean;
  borderRadius?: StyledButtonGroupBorderRadius;
  floatLabel?: string;
  withoutElevation?: boolean;
}

export const Container = styled.div<StyledButtonGroup>`
  display: flex;
  gap: ${({ gap }) => `${gap}px`};
  justify-content: ${({ align }) => align};
  flex-direction: ${({ disposition }) =>
    disposition === 'horizontal' ? 'row' : 'column'};
  width: 100%;
  height: 100%;
`;

export const ButtonWrapper = styled.div<StyledButtonWrapper>`
  position: relative;

  .BaSe--icon-button,
  .BaSe--button {
    ${({ firstItem, lastItem }) =>
      !firstItem &&
      !lastItem &&
      css`
        border-radius: 0;
      `}

    ${({ firstItem, lastItem, disposition, borderRadius, gap }) =>
      disposition === 'horizontal'
        ? css`
            ${borderRadius
              ? css`
                  ${firstItem &&
                  css`
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    border-top-left-radius: ${numberWithUnit(borderRadius)};
                    border-bottom-left-radius: ${numberWithUnit(borderRadius)};
                  `}
                  ${lastItem &&
                  css`
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-top-right-radius: ${numberWithUnit(borderRadius)};
                    border-bottom-right-radius: ${numberWithUnit(borderRadius)};
                  `}
                `
              : css`
                  ${firstItem &&
                  css`
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                  `}
                  ${lastItem &&
                  css`
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                  `}
                `}

            ${!lastItem &&
            !gap &&
            css`
              border-right: none;
            `};
          `
        : css`
            ${borderRadius
              ? css`
                  ${firstItem &&
                  css`
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                    border-top-left-radius: ${numberWithUnit(borderRadius)};
                    border-top-right-radius: ${numberWithUnit(borderRadius)};
                  `}
                  ${lastItem &&
                  css`
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                    border-bottom-left-radius: ${numberWithUnit(borderRadius)};
                    border-bottom-right-radius: ${numberWithUnit(borderRadius)};
                  `}
                `
              : css`
                  ${firstItem &&
                  css`
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                  `}
                  ${lastItem &&
                  css`
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                  `}
                `}

            ${!lastItem &&
            !gap &&
            css`
              border-bottom: none;
            `};
          `};
  }

  ${({ floatLabel, gap }) =>
    floatLabel &&
    css`
      &::after {
        content: '${floatLabel}';
        position: absolute;
        left: calc(100% + (${gap}px / 2));
        top: 50%;
        transform: translate(-50%, -50%);
        will-change: transform;
        pointer-events: none;

        display: inline-flex;
        align-items: center;
        justify-content: center;

        padding: 0.5em 0.5em 0.6em;
        border-radius: 999px;
        background: ${BaSeTheme.colors.defaultColors.white};
        box-shadow: ${BaSeTheme.shadows.profundidade2};
        color: ${BaSeTheme.colors.institucionais.cinzaSebrae60};

        font-size: 0.6em;
        font-weight: normal;
        font-family: Figtree, 'Open Sans', 'Helvetica Neue', Helvetica, Arial,
          sans-serif;

        white-space: nowrap;
        z-index: 2;
      }
    `}

  .BaSe--icon-button:focus,
  .BaSe--button:focus {
    z-index: 1;
  }

  .BaSe--button:not(:focus) {
    ${({ withoutElevation }) =>
      withoutElevation &&
      css`
        box-shadow: ${BaSeTheme.shadows.profundidade0};
      `}
  }
`;
